import type { Action } from "@/components/MenuButton";
import { MaterialNames } from "@/components/material_symbol_names";
import { useCanManageInteractionsBool, useCanManageTasksBool } from "@/hooks/useCapabilities";
import { useQuery } from "@apollo/client";
import { PERSON_DETAILS_HEADER_QUERY } from "../queries";
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { isDev } from "@/lib/urlUtils";
import useAmplitude from "@/hooks/useAmplitude";
import { withPersonContactMenuTracking } from "@/lib/quickActionMenuAmplitude";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { useTopActionBar, SideBar } from "@/layouts/AppLayout/TopActionBarContext";
import { buildPersonContactMenuItems } from "../utils/personContactMenuItems";

export const CCMS_CLICK_EVENT = "click person ccms link";

export const getCcmsUrl = (pgaId: string) =>
  isDev()
    ? `https://ccms-pgastage.dev.pga.org/prod/cencustmast.master_pg?q_cust_id=${pgaId}`
    : `https://sms.pgalinks.com/prod/cencustmast.master_pg?q_cust_id=${pgaId}`;

export interface PersonActionButton {
  text: string;
  iconName?: MaterialNames;
  onClick: () => void;
  testId?: string;
  variant?: "text" | "outlined" | "filled";
}

export const getPersonActionButtons = (
  pgaId: string,
  canManageInteractions: boolean,
  customAddInteractionHandler?: () => void,
  onCCMSClick?: () => void,
  universityLegacyId?: string
): PersonActionButton[] => {
  const handleCCMSClick = () => {
    onCCMSClick?.();
    window.open(getCcmsUrl(pgaId), "_blank");
  };

  const handleViewUniversityStudentProfile = () => {
    if (universityLegacyId) {
      window.open(`/education/universities/${universityLegacyId}/students/${pgaId}`, "_blank");
    }
  };

  return [
    ...(canManageInteractions
      ? [
          {
            text: "Add Interaction",
            iconName: "add_comment" as MaterialNames,
            onClick: customAddInteractionHandler || (() => {}),
            testId: "add-interaction-button",
            variant: "outlined" as const
          }
        ]
      : []),
    ...(universityLegacyId
      ? [
          {
            text: "View Student Profile",
            iconName: "open_in_new" as MaterialNames,
            onClick: handleViewUniversityStudentProfile,
            testId: "view-student-profile-button",
            variant: "text" as const
          }
        ]
      : [
          {
            text: "CCMS",
            iconName: "open_in_new" as MaterialNames,
            onClick: handleCCMSClick,
            variant: "text" as const
          }
        ])
  ];
};

/**
 * Custom hook to centralize person action button logic.
 */
export const usePersonActions = (pgaId: string) => {
  const canManageInteractions = useCanManageInteractionsBool();
  const canManageTasks = useCanManageTasksBool();
  const { showSidebar } = useTopActionBar();
  const location = useLocation();
  const { trackEvent } = useAmplitude();

  const { data: personData } = useQuery(PERSON_DETAILS_HEADER_QUERY, {
    variables: { pgaId },
    skip: !pgaId
  });

  useEffect(() => {
    if (location.state?.autoOpenInteraction) {
      showSidebar(SideBar.CREATE_GLOBAL_INTERACTION)();
      window.history.replaceState({}, document.title);
    }
  }, [location.state, showSidebar]);

  const openGlobalInteraction = showSidebar(SideBar.CREATE_GLOBAL_INTERACTION);
  const openGlobalTask = showSidebar(SideBar.CREATE_GLOBAL_TASK);

  // Only active student `university` — lastAttended alone is not enough for View Student
  // Profile (student detail layout queries alumni: false and 404s for former students).
  const universityLegacyId = personData?.person?.university?.legacyId;

  const { isCompactLayout } = useIsCompactLayout();
  const isOnPersonTab = location.pathname.includes(`/people/${pgaId}/person`);

  const baseActionButtons = getPersonActionButtons(
    pgaId,
    false,
    openGlobalInteraction,
    () => trackEvent(CCMS_CLICK_EVENT),
    universityLegacyId
  );

  const personActionButtons = isCompactLayout
    ? baseActionButtons.filter((button) => button.text !== "CCMS")
    : baseActionButtons;

  const person = personData?.person;

  const contactActions = useMemo(() => {
    if (!isOnPersonTab || !person) return [];

    const items = buildPersonContactMenuItems(person);
    const hasContactMethods = items.length > 0;

    if (canManageInteractions) {
      items.push({
        text: "Add Interaction",
        iconName: "add_comment",
        onClick: openGlobalInteraction,
        testId: "contact-add-interaction",
        menuOnly: true,
        dividerAbove: hasContactMethods
      });
    }

    if (canManageTasks) {
      items.push({
        text: "Add Task",
        iconName: "inventory",
        onClick: openGlobalTask,
        testId: "contact-add-task",
        menuOnly: true,
        dividerAbove: hasContactMethods && !canManageInteractions
      });
    }

    return items.map((item) => withPersonContactMenuTracking(item, pgaId, trackEvent));
  }, [
    canManageInteractions,
    canManageTasks,
    isOnPersonTab,
    openGlobalInteraction,
    openGlobalTask,
    pgaId,
    person,
    trackEvent
  ]);

  const actionButtons: Action[] = [...personActionButtons, ...contactActions];

  const menuButtonLabel = contactActions.length > 0 && personActionButtons.length < 3 ? "Contact" : undefined;

  return {
    actionButtons,
    menuButtonLabel,
    personData: person
  };
};
